import { cn } from "~/lib/utils";
import { iconForMime, resolveVaultMimeType, typeLabel } from "../vault/vaultShared";

export type ContextScenarioFile = {
  readonly name: string;
  readonly mimeType?: string;
  readonly path?: string;
};

/** macOS Finder-style compact file icon preview for context panel cards. */
export function ContextFileIconPreview({
  file,
  className,
  size = "md",
}: {
  readonly file: ContextScenarioFile;
  readonly className?: string;
  readonly size?: "sm" | "md" | "lg";
}) {
  const mimeType = resolveVaultMimeType({
    name: file.name,
    mimeType: file.mimeType ?? "application/octet-stream",
  });
  const Icon = iconForMime(mimeType);
  const label = typeLabel(mimeType);

  if (size === "lg") {
    return (
      <div
        className={cn(
          "relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden rounded-lg bg-[#e8e8ed] dark:bg-muted/35",
          className,
        )}
      >
        <div className="flex size-16 items-center justify-center rounded-[18%] bg-white shadow-[0_3px_10px_rgba(0,0,0,0.12)] ring-1 ring-black/5 dark:bg-[#fcfcfc] dark:shadow-[0_3px_12px_rgba(0,0,0,0.35)]">
          <Icon className="size-7 text-muted-foreground" aria-hidden="true" />
        </div>
        <div className="absolute inset-x-0 bottom-0 border-t border-black/5 bg-white/90 px-2 py-1.5 dark:border-white/10 dark:bg-background/90">
          <p className="truncate text-[11px] font-medium text-foreground">{file.name}</p>
          <p className="truncate text-[10px] text-muted-foreground">
            {file.path ? `${file.path} · ` : ""}
            {label}
          </p>
        </div>
      </div>
    );
  }

  const shellSize = size === "sm" ? "size-4" : "size-5";
  const iconSize = size === "sm" ? "size-2" : "size-2.5";

  return (
    <span
      className={cn(
        "relative flex shrink-0 items-center justify-center rounded-[4px] bg-[#e8e8ed] dark:bg-muted/40",
        shellSize,
        className,
      )}
      title={file.name}
    >
      <span className="flex size-[78%] items-center justify-center rounded-[18%] bg-white shadow-[0_1px_4px_rgba(0,0,0,0.1)] ring-1 ring-black/5 dark:bg-[#fcfcfc]">
        <Icon className={cn(iconSize, "text-muted-foreground")} aria-hidden="true" />
      </span>
    </span>
  );
}
